// v2 acacia — a forking savanna skeleton: a short bole splits into three angled
// limbs, each running on into a thinner outer branch, together carrying the
// iconic umbrella in two stacked layers (a broad ragged lower canopy ring and a
// smaller sun-bleached top layer, dead flat on top) dotted with pale blossom
// puffs along the rim. Variants re-deal the fork yaws and spin the canopy.
// Ported 1:1 from age-of-mcp's v2/tree-acacia.ts, using the library's shared
// `tropic` (h01, lerp, tube, tuft) helpers.
import * as THREE from "three";
import { defineModel, tropic, type Part } from "../../lib";

const BARK = "#7a5c38";
const BARK_OUT = "#6b4f30";
const CAN = "#6f8f3a";
const CAN_2 = "#7fa044";
const CAN_TOP = "#94aa52";
const CAN_TOP_2 = "#86a04a";
const BLOOM = "#e8d27a";

function acaciaParts(v: number): Part[] {
  const seed = v * 19 + 11;
  const yaw = v * 2.0 + tropic.h01(seed, 0) * 0.7;
  const parts: Part[] = [
    // short bole, then the skeleton forks
    tropic.tube([0, 0, 0], [0.04, 0.5, 0.02], 0.13, 0.095, BARK, 6),
  ];
  // Three limbs fanning up to the canopy underside, each forking on into
  // a thin outer branch.
  for (let i = 0; i < 3; i++) {
    const a = yaw + i * 2.1 + tropic.h01(seed, 10 + i) * 0.5;
    const mx = Math.cos(a) * 0.4, mz = Math.sin(a) * 0.4;
    parts.push(tropic.tube([0.04, 0.47, 0.02], [mx, 1.18, mz], 0.055, 0.035, BARK, 5));
    const oa = a + tropic.lerp(-0.45, 0.45, tropic.h01(seed, 20 + i));
    parts.push(tropic.tube([mx, 1.16, mz],
      [Math.cos(oa) * 0.62, 1.4, Math.sin(oa) * 0.62], 0.03, 0.012, BARK_OUT, 4));
  }
  // Lower canopy layer — a broad ragged ring of flattened tufts.
  for (let i = 0; i < 6; i++) {
    const a = yaw + 0.3 + (i / 6) * Math.PI * 2 + tropic.h01(seed, 30 + i) * 0.4;
    const r = tropic.lerp(0.26, 0.32, tropic.h01(seed, 40 + i));
    parts.push(tropic.tuft(Math.cos(a) * 0.5, 1.42, Math.sin(a) * 0.5, r, 0.34,
      i % 2 ? CAN : CAN_2, a));
  }
  parts.push(tropic.tuft(0, 1.45, 0, 0.4, 0.32, CAN_2, yaw));
  // Upper layer — smaller, bleached by the sun, keeping the top dead flat.
  for (let i = 0; i < 3; i++) {
    const a = yaw + 0.9 + (i / 3) * Math.PI * 2;
    parts.push(tropic.tuft(Math.cos(a) * 0.24, 1.56, Math.sin(a) * 0.24, 0.24, 0.32,
      i % 2 ? CAN_TOP : CAN_TOP_2, a));
  }
  parts.push(tropic.tuft(0.02, 1.58, -0.02, 0.28, 0.3, CAN_TOP, yaw + 0.5));
  // Pale blossom puffs dotting the canopy rim.
  for (let i = 0; i < 3; i++) {
    const a = yaw + 0.7 + i * 2.2 + tropic.h01(seed, 50 + i) * 0.6;
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.04, 0),
      color: BLOOM,
      position: [Math.cos(a) * 0.66, 1.5, Math.sin(a) * 0.66] });
  }
  return parts;
}

export const treeAcaciaV2 = defineModel({
  id: "tree-acacia-v2",
  batchId: "fable",
  name: "Tree acacia (v2)",
  tags: ["scenery","tree","acacia","low-poly","age-of-mcp","fable"],
  summary: "Low-poly tree acacia.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.75,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => acaciaParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => acaciaParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => acaciaParts(2) },
  ],
  animations: [],
});
